import {endOfDay, startOfDay} from 'date-fns';
import {combine, guard, merge, sample} from 'effector';

import {RestError, showRestError} from 'core/errors';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {
    AppealStatus,
    AppealExportFormat,
    AppealsRegistryRequest,
    ComplaintsAccessors,
    InnerFilterValue,
    InnerFilters,
} from 'types';
import {mapSortings} from 'utils/grid-mappers';

import {
    branches$,
    data$,
    defaultFilters,
    defaultSortings,
    employees$,
    exportDataFx,
    exportRequested,
    fetchBranchesFx,
    fetchDataFx,
    fetchEmployeesFx,
    fetchThemesFx,
    filters$,
    filtersChanged,
    filtersReset,
    Gate,
    pageNumber$,
    pageSize$,
    sortings$,
    sortingsChanged,
    sortingsReset,
    stateReset,
    tableStateChanged,
    themes$,
} from './index';

import './detail/init';
import './create/init';

const getOptionValue = (value: InnerFilterValue) => {
    if (!value || Array.isArray(value) || value instanceof Date || typeof value !== 'object') {
        return undefined;
    }

    return value.value;
};

const getTextValue = (value: InnerFilterValue) => {
    const text = typeof value === 'string' ? value.trim() : undefined;
    return text || undefined;
};

const createRequest = (
    pageNumber: number,
    pageSize: number,
    filters: InnerFilters,
    sortings: Record<string, boolean | undefined>,
): AppealsRegistryRequest => {
    const requestSortings = mapSortings(sortings).filter(
        (sorting): sorting is {id: string; desc: boolean} => typeof sorting.desc === 'boolean',
    );
    const request: AppealsRegistryRequest = {
        paging: {pageNumber, pageSize},
        sortings: requestSortings,
    };
    const theme = getOptionValue(filters[ComplaintsAccessors.theme]);
    const statuses = filters[ComplaintsAccessors.status];
    const dateRange = filters[ComplaintsAccessors.createdAt];
    const assignedTo = Number(getOptionValue(filters[ComplaintsAccessors.assigneeName]));
    const branchId = Number(getOptionValue(filters[ComplaintsAccessors.branchName]));
    const store = getTextValue(filters[ComplaintsAccessors.store]);
    const markingCode = getTextValue(filters[ComplaintsAccessors.markingCode]);
    const slaOverdue = getOptionValue(filters[ComplaintsAccessors.slaIndicator]);
    const codeNotFound = getOptionValue(filters[ComplaintsAccessors.codeNotFound]);

    if (typeof theme === 'string' && theme) request.themes = [theme];
    if (Array.isArray(statuses) && statuses.length) request.statuses = statuses as AppealStatus[];
    if (Array.isArray(dateRange)) {
        const [dateFrom, dateTo] = dateRange;
        if (dateFrom instanceof Date) request.dateFrom = startOfDay(dateFrom).toISOString();
        if (dateTo instanceof Date) request.dateTo = endOfDay(dateTo).toISOString();
    }
    if (Number.isFinite(assignedTo)) request.assignedTo = assignedTo;
    if (Number.isFinite(branchId)) request.branchId = branchId;
    if (store) request.store = store;
    if (markingCode) request.markingCode = markingCode;
    if (typeof slaOverdue === 'boolean') request.slaOverdue = slaOverdue;
    if (typeof codeNotFound === 'boolean') request.codeNotFound = codeNotFound;

    return request;
};

const requestSource = combine({
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    sortings: sortings$,
});

data$.on(fetchDataFx.doneData, (_, data) => data).reset(Gate.close);
employees$.on(fetchEmployeesFx.doneData, (_, data) => data).reset(Gate.close);
branches$.on(fetchBranchesFx.doneData, (_, data) => data).reset(Gate.close);
themes$.on(fetchThemesFx.doneData, (_, data) => data).reset(Gate.close);
pageNumber$.reset(stateReset, Gate.close);
pageSize$.reset(stateReset, Gate.close);

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

sample({
    clock: merge([Gate.open, tableStateChanged]),
    source: requestSource,
    fn: ({pageNumber, pageSize, filters, sortings}) =>
        createRequest(pageNumber, pageSize, filters, sortings),
    target: fetchDataFx,
});

sample({clock: Gate.open, target: fetchThemesFx});

const employeesRequested = guard({
    clock: Gate.open,
    filter: ({canViewPersonnel}) => canViewPersonnel,
});

const branchesRequested = guard({
    clock: Gate.open,
    filter: ({canManageBranches}) => canManageBranches,
});

sample({clock: employeesRequested, target: fetchEmployeesFx});
sample({clock: branchesRequested, target: fetchBranchesFx});

sample({
    clock: exportRequested,
    source: requestSource,
    fn: ({pageNumber, pageSize, filters, sortings}, format) => ({
        request: createRequest(pageNumber, pageSize, filters, sortings),
        format,
    }),
    target: exportDataFx,
});

const exportExtensions: Record<AppealExportFormat, string> = {
    [AppealExportFormat.CSV]: 'csv',
    [AppealExportFormat.EXCEL]: 'xlsx',
    [AppealExportFormat.JSON]: 'json',
};

exportDataFx.done.watch(({params, result}) => {
    const url = URL.createObjectURL(result);
    const link = document.createElement('a');
    link.href = url;
    link.download = `complaints.${exportExtensions[params.format]}`;
    link.click();
    URL.revokeObjectURL(url);
});

[fetchDataFx, fetchEmployeesFx, fetchBranchesFx, fetchThemesFx, exportDataFx].forEach((effect) =>
    effect.failData.watch((error) => showRestError(error as RestError)),
);
